/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useIntl } from 'react-intl-next';

import { cssMap, jsx } from '@atlaskit/css';
import EditIcon from '@atlaskit/icon/core/migration/edit';
import EditorFeedbackIcon from '@atlaskit/icon/core/migration/feedback--editor-feedback';
import Modal, { ModalBody, ModalHeader, ModalTitle } from '@atlaskit/modal-dialog';
import { Box, Flex, Pressable } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { messages } from './messages';

const styles = cssMap({
	feedbackOptionsWrapper: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.200'),
		paddingBottom: token('space.300'),
	},
	feedbackOption: {
		borderRadius: token('radius.small'),
		width: '100%',
		height: '56px',
		backgroundColor: token('color.background.neutral.subtle'),
		'&:hover': {
			backgroundColor: token('color.background.neutral.subtle.hovered'),
		},
	},
	feedbackOptionIconWrapper: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		borderRadius: token('radius.xsmall'),
		height: '32px',
		width: '32px',
	},
	feedbackOptionText: {
		font: token('font.body'),
		color: token('color.text'),
	},
	feedbackOptionSubText: {
		font: token('font.body.small'),
		color: token('color.text.subtlest'),
	},
});

type FeedbackOptionModalProps = {
	setShowFeedbackOptionsModal: (showModal: boolean) => void;
	setShowEditDefinitionModal: (showModal: boolean) => void;
	setShowFeedbackModal: (showModal: boolean) => void;
	editDefinitionEnabled: boolean;
};

export const FeedbackOptionsModal = ({
	setShowFeedbackOptionsModal,
	setShowEditDefinitionModal,
	setShowFeedbackModal,
	editDefinitionEnabled,
}: FeedbackOptionModalProps) => {
	const { formatMessage } = useIntl();

	return (
		<Modal onClose={() => setShowFeedbackOptionsModal(false)} shouldReturnFocus={false}>
			<ModalHeader hasCloseButton>
				<ModalTitle>
					{formatMessage(
						editDefinitionEnabled
							? messages.definition_footer_feedback_options_modal_header
							: messages.definition_footer_feedback_options_modal_header_feedback_only,
					)}
				</ModalTitle>
			</ModalHeader>
			<ModalBody>
				<Box xcss={styles.feedbackOptionsWrapper}>
					{editDefinitionEnabled && (
						<Pressable
							xcss={styles.feedbackOption}
							onClick={() => {
								setShowFeedbackOptionsModal(false);
								setShowEditDefinitionModal(true);
							}}
						>
							<Flex gap="space.100">
								<Box
									backgroundColor="color.background.accent.blue.subtle"
									xcss={styles.feedbackOptionIconWrapper}
								>
									<EditIcon
										LEGACY_size="medium"
										spacing="spacious"
										label=""
										color={token('color.icon.inverse', '#FFFFFF')}
									/>
								</Box>
								<Flex direction="column" gap="space.025" alignItems="start">
									<Box xcss={styles.feedbackOptionText}>
										{formatMessage(
											messages.definition_footer_feedback_options_edit_definition_option,
										)}
									</Box>
									<Box xcss={styles.feedbackOptionSubText}>
										{formatMessage(
											messages.definition_footer_feedback_options_edit_definition_option_subtext,
										)}
									</Box>
								</Flex>
							</Flex>
						</Pressable>
					)}
					<Pressable
						xcss={styles.feedbackOption}
						onClick={() => {
							setShowFeedbackOptionsModal(false);
							setShowFeedbackModal(true);
						}}
					>
						<Flex gap="space.100">
							<Box
								backgroundColor="color.background.accent.blue.subtle"
								xcss={styles.feedbackOptionIconWrapper}
							>
								<EditorFeedbackIcon
									LEGACY_size="medium"
									spacing="spacious"
									label=""
									color={token('color.icon.inverse', '#FFFFFF')}
								/>
							</Box>
							<Flex direction="column" gap="space.025" alignItems="start">
								<Box xcss={styles.feedbackOptionText}>
									{formatMessage(messages.definition_footer_feedback_options_leave_feedback_option)}
								</Box>
								<Box xcss={styles.feedbackOptionSubText}>
									{formatMessage(
										messages.definition_footer_feedback_options_leave_feedback_option_subtext,
									)}
								</Box>
							</Flex>
						</Flex>
					</Pressable>
				</Box>
			</ModalBody>
		</Modal>
	);
};
